import React, {useCallback, useEffect, useMemo, useReducer} from 'react';
import {Helmet} from 'react-helmet';
import {NavLink} from 'react-router-dom';
import {CSSTransition} from 'react-transition-group';
import {compose} from 'redux';

import './styles.css';
import BasicButton from '../../components/BasicButton';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import JobContentLoader from '../../components/JobContentLoader';
import JobDetails from '../../components/JobDetails';
import PanelContainer from '../../components/PanelContainer';
import ShareButton from '../../components/ShareButton';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import ROUTE from '../../constants/route';
import LinkBuilder from '../../helpers/link-builder';
import {linkedInShareUrl} from '../../helpers/share';
import StringHelpers from '../../helpers/string-helpers';
import withProcessApplyAction from '../../hocs/withProcessApplyAction';
import useApi from '../../hooks/useApi';
import PageLayout from '../../layouts/PageLayout';
import ApplyJobConfirmDialog from '../../scenes/Dialog/components/ApplyJobConfirmDialog';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import Sidebar from '../../scenes/Sidebar';
import InBounds from '../../scenes/Sidebar/components/InBounds';
import {action} from './state/actions';
import {initialState} from './state/initialState';
import {reducer} from './state/reducer';

const Job = props => {
  const {
    user,
    history,
    location,
    globalActions: { updateUserInboundsCount },
    applyActions,
    match: {
      params: { id },
    },
  } = props;

  const api = useApi();
  const [state, dispatch] = useReducer(reducer, initialState);

  const processStatus = useMemo(
    () =>
      state.process
        ? state.process.Status
          ? 'closed'
          : state.process.isApplied
          ? 'applied'
          : null
        : null,
    [state.process]
  );

  useEffect(() => {
    dispatch(action.processRequestData());
    api.get(
      ENDPOINT.ENTITY.JOB,
      { id },
      {
        handleError: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: process => {
          dispatch(action.processSetData(process));

          if (location.search) {
            const params = new URLSearchParams(location.search);
            const queryParamAction = params.get('action');
            const message = params.get('message');

            if ('apply' === queryParamAction) {
              history.replace(location.pathname);
              applyJob(process);
            } else if ('not-interested' === queryParamAction) {
              history.replace(location.pathname);
              applyJob(process, false);
            } else if (message) {
              history.replace(history.location.pathname);
              let text = '';

              if ('processInterestedInRole' === message) {
                text = `Thank you! A recruiter will be notified that you are interested in ${
                  process.PositionTitle
                } Role and get in touch with you soon.`;
              }

              if ('processNotInterestedInRole' === message) {
                text = `A recruiter will be notified that you are not interested in ${
                  process.PositionTitle
                } Role.`;
              }

              if (!!text) {
                BasicDialog({
                  bodyRender: text,
                  popupStyles: {
                    maxWidth: '642px',
                    padding: '30px 25px 30px 25px',
                  },
                });
              }
            }
          }
        },
      }
    );
  }, [history.location]);

  const applyJobStart = useCallback((process) => {
    ApplyJobConfirmDialog({id: process.Id})
      .then(() => {
        applyJob(process);
      })
      .catch(() => {
        applyJob(process, false);
      });
  }, []);

  const applyJob = useCallback((process, interested = true) => {
    applyActions
      .apply(process, interested)
      .then(success => {
        if (success) {
          dispatch(action.processSetApplied(true));
          if (interested) {
            BasicDialog({ bodyRender: `Thank you! A recruiter will be notified that you are interested in ${process.PositionTitle} Role and get in touch with you soon.` });
          } else {
            BasicDialog({ bodyRender: `A recruiter will be notified that you are not interested in ${process.PositionTitle} Role.` });
          }
        }
      });
  }, []);

  const handleApplyJob = () => {
    applyJobStart(state.process);
  };

  const openLinkedInSharingWindow = () =>
    linkedInShareUrl(
      window.location.origin + LinkBuilder.getProcessPublicLink(state.process.Id, 'process')
    );

  return (
    <PageLayout
      pageClass="job-page"
      mainContent={
        state.init && state.process ? (
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <PanelContainer>
              <Helmet>
                <title>{state.process.PositionTitle}</title>
              </Helmet>
              <JobDetails job={state.process} />

              <div className="job-controls">
                {processStatus ? (
                  <div className="job-controls__disabled-btn">
                    {StringHelpers.ucfirst(processStatus)}
                  </div>
                ) : (
                  <div className="job-controls__apply-btn">
                    <BasicButton
                      disabled={state.isApplyButtonDisabled}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={600}
                      btnText="Begin Application"
                      btnArrow={true}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleApplyJob}
                    />
                  </div>
                )}

                <NavLink to={ROUTE.JOBS} className="job-controls__back-btn">
                  Back
                </NavLink>

                {state.process.BlockLinkedInSharing === 'No' && (
                  <ShareButton
                    caption="Share on Linkedin"
                    separatorLeft={true}
                    action={openLinkedInSharingWindow}
                  />
                )}
              </div>
            </PanelContainer>
          </CSSTransition>
        ) : (
          <div className="horizontal-loader__container">
            <JobContentLoader />
          </div>
        )
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          {state.init ? (
            <Sidebar>
              {
                <InBounds
                  inboundsCount={user.inbounds}
                  onInboundsUpdate={updateUserInboundsCount}
                />
              }
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )}
        </CSSTransition>
      }
    />
  );
};

export default compose(withProcessApplyAction)(Job);
